import React, { useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'core/utils/dayjs';
import { useSelector, useDispatch } from 'react-redux';
import Popup from 'core/modules/Popup/Popup';
import { selectSiteUrl } from 'core/selectors/app';
import { useGetBonusOffers } from 'core/modules/BonusOffers/hooks';
import { useGetButtonUrl } from 'core/modules/BonusButton/hooks/useGetButtonUrl';
import { parseBonusButton } from 'core/modules/BonusButton/utils/parseBonusButton';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import { addCommas } from 'core/utils/helpers/numbers';
import {
  triggerBonusButtonPopupIsCollapsedGaEvent, triggerBonusButtonModalIsExpandedGaEvent,
  triggerBonusButtonPopupIsViewedGaEvent,
} from 'core/modules/BonusButton/actions';
import { useHandlePopupCookie } from './hooks/useHandlePopupCookie';
import config from '../../configs';

import './BonusButtonPopup.scss';

function BonusButtonPopup({ popup }) {
  const { params = {} } = popup;
  const { isQualified } = params;
  const buttonUrl = useGetButtonUrl();

  const dispatch = useDispatch();
  const siteUrl = useSelector(selectSiteUrl);
  const {
    dateFormat, getBonusText, getTermsText, getConfirmationText,
  } = config.popupConfig;

  const bonusText = getBonusText();
  const termsText = getTermsText();
  const confirmationText = getConfirmationText();

  const { bonusButtonPopup } = useGetBonusOffers();
  const { postInstallPopupWasShown, preInstallPopupWasShown } = useHandlePopupCookie(isQualified);

  const {
    endDate, maxRewardAvailable, minSpendToQualify, brandCurrency, leftAmount, totalAmountSpentNumber,
  } = parseBonusButton(bonusButtonPopup);

  useEffect(() => {
    if (bonusButtonPopup) {
      dispatch(triggerBonusButtonPopupIsViewedGaEvent(isQualified, leftAmount));
    }
  }, [bonusButtonPopup]);

  if (isEmpty(bonusButtonPopup)) {
    return null;
  }

  const onCollapseCallback = () => dispatch(triggerBonusButtonPopupIsCollapsedGaEvent(isQualified));
  const onExpandCallback = () => dispatch(triggerBonusButtonModalIsExpandedGaEvent(isQualified));

  const leftAmountCeiled = Math.ceil(leftAmount);
  const endDateFormated = dayjs(endDate).format(dateFormat);

  if (isQualified) {
    return (
      <Popup
        className="mn_bonusButtonPopup mn_postInstall"
        collapsedContent={
          <>
            <span>{addCommas(maxRewardAvailable)} bonus {`${brandCurrency}`}<span className="mn_dot">.</span></span>
            <span className="mn_icon" />
          </>
        }
        isExpandedByDefault={!postInstallPopupWasShown}
        onCollapseCallback={onCollapseCallback}
        onExpandCallback={onExpandCallback}
      >
        <div className="mn_confirmation">{confirmationText}</div>
        <div className="mn_title">
          Now spend ${leftAmountCeiled} {totalAmountSpentNumber > 0 ? 'more' : ''} by{' '}
          {endDateFormated} to earn {addCommas(maxRewardAvailable)} {bonusText} {brandCurrency}.
          <span className="mn_asterisk">*</span>
        </div>
        <div className="mn_ctaAndTerms">
          <a
            className="mn_button mn_secondaryButton mn_getButton"
            href={`${siteUrl}/p/extra-rewards.htm`}
            data-left-amount={leftAmount}
          >
            Shop now
          </a>
          {termsText && <div className="mn_terms">{termsText}</div>}
        </div>
      </Popup>
    );
  }

  return (
    <Popup
      className="mn_bonusButtonPopup mn_preInstall"
      collapsedContent={
        <>
          <span>{addCommas(maxRewardAvailable)} bonus {`${brandCurrency}`}<span className="mn_dot">.</span></span>
          <span className="mn_icon" />
        </>
      }
      isExpandedByDefault={!preInstallPopupWasShown}
      onCollapseCallback={onCollapseCallback}
      onExpandCallback={onExpandCallback}
    >
      <div className="mn_title">
        Earn {addCommas(maxRewardAvailable)} {bonusText} {brandCurrency}<span className="mn_dot">.</span>
      </div>
      <div className="mn_subTitle">
        Get the button and spend {minSpendToQualify} by {endDateFormated} to earn.<span className="mn_sterisk">*</span>
      </div>
      <div className="mn_ctaAndTerms">
        <a className="mn_button mn_secondaryButton mn_getButton" href={buttonUrl}>Get the button</a>
        {termsText && <div className="mn_terms">{termsText}</div>}
      </div>
    </Popup>
  );
}

export default (props) => <ComponentErrorBoundary><BonusButtonPopup {...props} /></ComponentErrorBoundary>;
